import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { X, Calculator, Eye, EyeOff } from "lucide-react";
import { formatInTimeZone } from "date-fns-tz";
import libFloat from "../../../../util/libFloat";
import Installment from "../../../../components/installment/parameters";
import MessageDialog from "../../../../components/ui/dialog/MessageDialog";
import LoadingFull from "../../../../components/loading/loading-full";

// Fora do fluxo de edição de itens, o total da venda já é fixo — só existe uma operação
// real: recalcular o cronograma inteiro pra esse valor. "Criar mais parcelas" só passa a
// fazer sentido quando um produto novo é adicionado e o total muda (feature futura).
const TIPO_OPERACAO = "REFAZER PARCELAMENTO";

export default function ModalRefazerParcelas({
	isOpen,
	onClose,
	venda,
	originalParcelas,
	onSave,
	saving,
}) {
	const [totalAParcelar, setTotalAParcelar] = useState(0);
	const [showOriginal, setShowOriginal] = useState(false);
	const [pendingParcelas, setPendingParcelas] = useState(null);
	const [isConfirmSaveOpen, setIsConfirmSaveOpen] = useState(false);

	// venda.valor já é o líquido (itens - desconto) a ser coberto pelas parcelas
	useEffect(() => {
		if (!venda) return;
		setTotalAParcelar(Number(venda.valor || 0));
	}, [venda, isOpen]);

	// Fecha a visualização das parcelas atuais e qualquer confirmação pendente toda vez que o modal é reaberto
	useEffect(() => {
		if (isOpen) {
			setShowOriginal(false);
			setIsConfirmSaveOpen(false);
			setPendingParcelas(null);
		}
	}, [isOpen]);

	// Recebe as parcelas já geradas e confirmadas pelo componente padrão de parcelamento
	// (o mesmo usado em Contas a Receber/Pagar), normaliza "ativo" (boolean lá) para o
	// padrão SIM/NAO usado no restante do sistema, e pede confirmação antes de mandar pro servidor.
	const handleParcelasGeradas = (data) => {
		const parcelasNormalizadas = (data.parcelas || []).map((p) => ({
			...p,
			ativo: p.ativo ? "SIM" : "NAO",
		}));
		setPendingParcelas(parcelasNormalizadas);
		setIsConfirmSaveOpen(true);
	};

	const handleConfirmSave = () => {
		if (!pendingParcelas) return;
		onSave(TIPO_OPERACAO, pendingParcelas);
		setIsConfirmSaveOpen(false);
	};

	const handleCancelSave = () => {
		setIsConfirmSaveOpen(false);
		setPendingParcelas(null);
	};

	const totalPendente = (pendingParcelas || []).reduce((acc, p) => acc + Number(p.valor || 0), 0);

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={onClose}
			overlayClassName="react-modal-overlay"
			className="react-modal-content-base w-full max-w-[1250px] h-[95vh] bg-white p-4 rounded-[28px] shadow-2xl relative animate-fade-in flex flex-col overflow-hidden"
			shouldCloseOnOverlayClick={false}
			ariaHideApp={false}
		>
			{saving && <LoadingFull />}
			<button
				onClick={onClose}
				className="absolute right-6 top-6 text-gray-400 hover:text-gray-600 transition-colors"
			>
				<X size={20} />
			</button>

			<div className="flex items-center gap-3 mb-2 border-b border-gray-100 pb-2 flex-shrink-0">
				<div className="bg-blue-50 text-blue-600 p-2.5 rounded-2xl">
					<Calculator size={22} />
				</div>
				<div>
					<h2 className="text-xl font-black text-gray-800 tracking-tight">Refazer Parcelas da Venda</h2>
					<p className="text-xs text-gray-500 font-medium">Configure e recalcule o parcelamento</p>
					<button
						type="button"
						onClick={() => setShowOriginal((prev) => !prev)}
						className="flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors uppercase mt-1"
					>
						{showOriginal ? <EyeOff size={14} /> : <Eye size={14} />}
						{showOriginal ? "Ocultar parcelas atuais" : "Ver parcelas atuais"}
					</button>
				</div>
			</div>

			{/* Resumo de valores */}
			<div className="grid grid-cols-3 gap-4 bg-gray-50/50 p-2 rounded-2xl border border-gray-100 mb-2 flex-shrink-0">
				<div>
					<span className="text-[10px] uppercase font-bold text-gray-400">Total Venda</span>
					<p className="text-base font-extrabold text-gray-700">{libFloat.strToCurrency(venda?.valor || 0)}</p>
				</div>
				<div>
					<span className="text-[10px] uppercase font-bold text-gray-400">Desconto</span>
					<p className="text-base font-extrabold text-red-500">{libFloat.strToCurrency(venda?.desconto || 0)}</p>
				</div>
				<div>
					<span className="text-[10px] uppercase font-bold text-gray-400">Total a Parcelar</span>
					<p className="text-base font-black text-blue-600">{libFloat.strToCurrency(totalAParcelar)}</p>
				</div>
			</div>

			{/* Consulta das parcelas atuais — só ocupa espaço quando expandida */}
			{showOriginal && (
				<div className="mb-3 flex-shrink-0 border border-gray-100 rounded-2xl overflow-hidden">
						<table className="w-full text-left border-collapse text-xs">
							<thead>
								<tr className="bg-gray-50 text-gray-500 font-bold border-b border-gray-100 uppercase text-[10px]">
									<th className="p-3">Parcela</th>
									<th className="p-3">Vencimento</th>
									<th className="p-3">Forma Pgto</th>
									<th className="p-3">Status</th>
									<th className="p-3 text-right">Valor</th>
								</tr>
							</thead>
							<tbody>
								{originalParcelas?.length > 0 ? (
									originalParcelas.map((p, index) => {
										const isPendente = p.ativo === "SIM" || p.ativo === true || p.ativo === 1;
										return (
											<tr key={index} className="border-b border-gray-50">
												<td className="p-3 font-bold text-gray-700">
													{p.documento ||
														(p.parcela
															? `${p.parcela}/${p.parcela_total ?? originalParcelas.length}`
															: `${index + 1}/${originalParcelas.length}`)}
												</td>
												<td className="p-3">
													{p.dataparcela || p.data
														? formatInTimeZone(new Date(p.dataparcela || p.data), "UTC", "dd/MM/yyyy")
														: "—"}
												</td>
												<td className="p-3">{p.forma || "—"}</td>
												<td
													className="p-3 font-bold"
													style={{ color: isPendente ? "#dc2626" : "#16a34a" }}
												>
													{isPendente ? "Pendente" : "Pago"}
												</td>
												<td className="p-3 text-right">{libFloat.strToCurrency(p.valor || 0)}</td>
											</tr>
										);
									})
								) : (
									<tr>
										<td colSpan={5} className="p-4 text-center text-gray-500">
											Nenhuma parcela atual encontrada.
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>
				)}

			{/* Gerador de parcelas padrão do sistema — mesmo componente usado em Contas a Receber/Pagar */}
			<div className="border-t border-gray-100 pt-1 flex-1 min-h-0 overflow-hidden flex flex-col">
				<Installment installmentValue={totalAParcelar} CallBackInstallments={handleParcelasGeradas} />
			</div>

			<div className="flex justify-end border-t border-gray-100 pt-2 mt-2 flex-shrink-0">
				<button
					onClick={onClose}
					className="h-11 px-6 border border-gray-200 rounded-[20px] text-xs font-bold text-gray-500 hover:bg-gray-50 active:scale-95 transition-all"
				>
					Fechar
				</button>
			</div>

			{/* Confirmação antes de enviar a alteração de parcelas pro servidor */}
			<MessageDialog
				open={isConfirmSaveOpen}
				title="Confirmar Alteração de Parcelas"
				description={`Deseja realmente refazer as parcelas da venda ${venda?.id_venda || ""}? Serão gravadas ${pendingParcelas?.length || 0} parcela(s) totalizando ${libFloat.strToCurrency(totalPendente)}.`}
				variant="question"
				buttons="confirmCancel"
				onConfirm={handleConfirmSave}
				onCancel={handleCancelSave}
				onClose={handleCancelSave}
			/>
		</Modal>
	);
}
